<template>
    <div
        ref="root"
        class="bg-muval-gray-6 relative grid size-full place-items-center overflow-hidden"
        data-test="file-thumbnail"
    >
        <img
            v-if="imageSrc"
            :src="imageSrc"
            :alt="label"
            loading="lazy"
            class="size-full object-cover"
            draggable="false"
            @error="onImageError"
        />
        <div
            v-else-if="source.kind === 'failed'"
            class="flex flex-col items-center gap-1 px-2 text-center"
            data-test="file-thumbnail-failed"
        >
            <SvgIcon
                name="system-warning"
                class="text-muval-red-1 size-10"
            />
            <span class="text-muval-gray-2 text-xs">{{ source.label }}</span>
        </div>
        <div
            v-else-if="source.kind === 'pending' || (needsSignedLink && !failed)"
            class="loader"
            data-test="file-thumbnail-pending"
        />
        <SvgIcon
            v-else
            :name="iconName"
            class="text-muval-gray-3 size-12"
        />
        <span
            v-if="extension"
            class="absolute top-2 left-2 rounded-xs px-1.5 py-px text-[10px] leading-tight font-medium text-white uppercase"
            :class="getExtensionBgClass(extension)"
            data-test="file-thumbnail-extension"
        >
            {{ extension }}
        </span>
    </div>
</template>

<script setup lang="ts">
import { useElementVisibility } from '@vueuse/core';
import SvgIcon from '@components/utilities/SvgIcon.vue';
import {
    getExtensionBgClass,
    getFileExtension,
    isImageFile,
    needsImagePreview,
    previewKind,
    THUMBNAIL_PENDING_MS,
    thumbnailSource,
} from '@utilities/fileDisplay';

/**
 * The picture on a file's card, filling the box it is given: the image's
 * thumbnail, why the API couldn't make one, a skeleton while a fresh upload's
 * thumbnail is built, or the file-type icon. The extension badge sits over every kind.
 */

const props = withDefaults(
    defineProps<{
        file: Record<string, any>;
        /** Alt text, matching the card's label. */
        label: string;
        /** The image has already failed after a refetch, so stop trying it. */
        failed?: boolean;
        /** False where `url` points at a private object, which only a signed link can read. */
        originalsReadable?: boolean;
        /** Fetches a signed link for an image whose original isn't readable. */
        resolveThumbnailUrl?: (file: Record<string, any>) => Promise<string>;
    }>(),
    { failed: false, originalsReadable: true, resolveThumbnailUrl: undefined },
);

const emit = defineEmits<{ error: [] }>();

// Bumped when a pending window runs out, so the slot falls through to the
// original without waiting for the list to change.
const now = ref(Date.now());

const source = computed(() => thumbnailSource(props.file, now.value, props.originalsReadable));

const iconName = computed(() => {
    if (isImageFile(props.file)) return 'system-photo';

    return previewKind(props.file) === 'video' ? 'system-play' : 'system-document';
});
const extension = computed(() => getFileExtension(props.file));

const root = ref<HTMLElement | null>(null);
const isVisible = useElementVisibility(root);
const signedSrc = ref<string | null>(null);
let linkRequests = 0;

// A private image with no thumbnail yet: the caller can sign one (the diary), so the card isn't left on the icon.
const wantsSignedLink = computed(
    () =>
        !!props.resolveThumbnailUrl &&
        source.value.kind === 'icon' &&
        isImageFile(props.file) &&
        !needsImagePreview(props.file),
);
const needsSignedLink = computed(() => wantsSignedLink.value && !signedSrc.value);

const requestLink = () => {
    linkRequests += 1;
    props.resolveThumbnailUrl!(props.file)
        .then((url) => (signedSrc.value = url))
        .catch(() => emit('error'));
};

// Asked for when the card is first seen, so a grid mounted out of sight (the diary's hidden Files tab) signs nothing.
watch(
    [wantsSignedLink, isVisible],
    ([wanted, visible]) => {
        if (wanted && visible && !linkRequests) requestLink();
    },
    { immediate: true },
);

// A signed link that expired before its image loaded (a card scrolled to late) is asked for once more.
const onImageError = () => {
    if (wantsSignedLink.value && signedSrc.value && linkRequests < 2) {
        signedSrc.value = null;
        requestLink();

        return;
    }

    emit('error');
};

const imageSrc = computed(() => {
    if (props.failed) return null;

    if (source.value.kind === 'thumb' || source.value.kind === 'original') return source.value.src;

    return wantsSignedLink.value ? signedSrc.value : null;
});

watchEffect((onCleanup) => {
    if (source.value.kind !== 'pending') return;

    const uploadedAt = new Date(props.file.created_at).getTime();
    const timer = setTimeout(
        () => {
            now.value = Date.now();
        },
        Math.max(0, uploadedAt + THUMBNAIL_PENDING_MS - Date.now()),
    );

    onCleanup(() => clearTimeout(timer));
});
</script>
